/**
 * The player core (PLAYER-9): one Shaka surface for every template and every
 * platform.
 *
 * Built once on purpose. There are nine templates; a player written per template
 * is nine decoder-discipline bugs, nine spinners that never resolve, and nine
 * different sentences for the same refusal. The Custom screen is the precedent -
 * one shared renderer, nine skins.
 *
 * ## Licence renewal is solved by NOT having a renewal
 *
 * A Verimatrix token lives **five minutes** (`verimatrixLicence.ts` -
 * `TOKEN_TTL_SECONDS`; the ticket said thirty, which would have made this less
 * urgent rather than more). A guest watching a two-hour film outlives twenty-four
 * of them.
 *
 * The obvious design is a timer that re-mints before expiry and re-configures
 * the player. It is the wrong one: it races the CDM, it keeps a credential alive
 * in memory between uses, and it fails silently when the tab is throttled.
 *
 * Instead a **request filter** rewrites the licence URL on every licence request
 * the CDM makes. The token is therefore minted at the instant it is used and is
 * never older than one round trip - so key rotation, a long film and a resumed
 * session are all the same case, and none of them is special. There is nothing
 * to renew because nothing is kept.
 *
 * ## Capability, never platform
 *
 * No `if (tizen)` anywhere, per the FoyerBridge rule. What varies is whether the
 * device can play encrypted media at all, and that is asked of the device
 * ({@link drmSupported}) rather than inferred from a user-agent.
 *
 * ⚠️ This surface plays through MSE/EME on a `<video>` element, which is what
 * the reference player does for linear channels on Tizen. It is NOT what a
 * Samsung LFD signage panel can do - that hardware refuses to decode through
 * `<video>` at all and needs AVPlay (see `panel-video-encoding` in memory). The
 * two surfaces are separate for that reason, and this one targets hospitality
 * panels and set-top boxes.
 */

/** Why playback stopped, in a form both a screen and a fleet console can use. */
export type StreamFailure =
  /** The licence server refused this request - entitlement, not plumbing. */
  | "licence_refused"
  /** A licence was held and has expired, and a fresh one could not be had. */
  | "licence_expired"
  /** The manifest could not be fetched. Delivery, not rights. */
  | "manifest_unreachable"
  /** The stream's codecs are not supported by this device. */
  | "codec_unsupported"
  /** The device has no decoder to give us - too many are already held. */
  | "decoder_exhausted"
  /** It started and then stopped, with bytes still owed. */
  | "network_stalled"
  /** Anything the classifier could not place. Never silently swallowed. */
  | "unknown";

/**
 * What a guest reads, ten feet away, with a remote in their hand.
 *
 * One sentence each, and deliberately not "an error occurred": the whole point
 * of the union above is that these five situations want five different actions
 * from whoever ends up dealing with them. Wording is plain rather than technical
 * because the person reading it is not the person who can fix it.
 */
export const STREAM_FAILURE_MESSAGE: Record<StreamFailure, string> = {
  licence_refused: "This title is not available on this television.",
  licence_expired: "This title stopped being available. Try starting it again.",
  manifest_unreachable: "This title could not be loaded. Check the connection.",
  codec_unsupported: "This television cannot play this title's video format.",
  decoder_exhausted: "The television ran out of video capacity. Try again.",
  network_stalled: "The connection stopped while playing.",
  unknown: "This title could not be played.",
};

/**
 * Shaka error codes, mapped to the union above.
 *
 * Only the ones we can place. Anything else becomes `unknown` rather than being
 * forced into a neighbouring bucket - a wrong name is worse than no name,
 * because it sends someone to fix the wrong thing.
 *
 * Codes from shaka-player's `shaka.util.Error.Code`. Written as numbers because
 * the enum lives inside the lazily-loaded bundle and this table must be
 * readable without it.
 *
 * ⚠️ Every number below was re-checked against
 * `node_modules/shaka-player/dist/shaka-player.compiled.d.ts` on 2026-08-16,
 * and **four of them were wrong** - each one named as its numeric neighbour.
 * The damage was exactly what the paragraph above predicts: `EXPIRED` is 6014
 * and not 6012, so the five-minute-token expiry this whole module is built
 * around classified as `unknown`, while a missing licence-server configuration
 * told a guest to "try starting it again". If you add a row here, read the
 * number out of the bundle. Do not trust a comment, including these.
 */
const SHAKA_CODE: Record<number, StreamFailure> = {
  1001: "manifest_unreachable", // BAD_HTTP_STATUS
  1002: "manifest_unreachable", // HTTP_ERROR
  1003: "manifest_unreachable", // TIMEOUT
  // VIDEO_ERROR - the `<video>` element's own MediaError, which covers a decode
  // failure AND a network abort AND a src that will not open. Left `unknown` on
  // purpose: it is the code a panel raises when it gives up on a rendition, and
  // guessing which of the three would send somebody to the wrong place with
  // confidence. `decoder_exhausted` (its previous name here) was the worst of
  // the three guesses - it accused the decoder pool of a fault it may not have.
  3016: "unknown",
  3017: "decoder_exhausted", // QUOTA_EXCEEDED_ERROR
  // RESTRICTIONS_CANNOT_BE_MET - every rendition in the manifest sits above the
  // cap {@link chooseAbrCap} set for this panel. Named `codec_unsupported`
  // because that is what it means to the person reading it: this television
  // cannot decode anything this title is packaged as. It is deliberately NOT a
  // new union member - the union is imported by the fleet console and by the
  // on-device probe, and a sixth code buys nothing the sentence does not say.
  4012: "codec_unsupported",
  4032: "codec_unsupported", // CONTENT_UNSUPPORTED_BY_BROWSER
  6001: "codec_unsupported", // REQUESTED_KEY_SYSTEM_CONFIG_UNAVAILABLE
  // FAILED_TO_ATTACH_TO_VIDEO - plumbing between the CDM and the element, not
  // an entitlement answer. Reporting it as `licence_refused` (as this table did)
  // sent an engineer to the rights desk over a broken `<video>` handle.
  6003: "unknown",
  6005: "licence_refused", // FAILED_TO_CREATE_SESSION
  6007: "licence_refused", // LICENSE_REQUEST_FAILED
  6008: "licence_refused", // LICENSE_RESPONSE_REJECTED
  // NO_LICENSE_SERVER_GIVEN - Foyer handed the player no server for a protected
  // stream. A configuration fault, but from the screen's point of view this
  // title cannot be licensed here, which is what `licence_refused` says.
  6012: "licence_refused",
  6014: "licence_expired", // EXPIRED
  7000: "network_stalled", // LOAD_INTERRUPTED
};

/** HTTP statuses that mean "you may not have this", not "we broke". */
const REFUSAL_STATUS = new Set([401, 402, 403]);

/**
 * Classify a Shaka error.
 *
 * A licence request that came back 401/403 is `licence_refused` whatever code
 * Shaka attached to it: the status is the more specific fact, and confusing an
 * entitlement refusal with a network fault is what sends an engineer to look at
 * a CDN while a guest is being denied a film they paid for.
 */
export function classifyStreamError(err: unknown): StreamFailure {
  const e = err as { code?: number; data?: unknown[] } | undefined;
  const status = (e?.data ?? []).find(
    (d): d is number => typeof d === "number" && d >= 100 && d < 600
  );
  if (status !== undefined && REFUSAL_STATUS.has(status)) {
    return "licence_refused";
  }
  if (typeof e?.code === "number" && SHAKA_CODE[e.code]) {
    return SHAKA_CODE[e.code]!;
  }
  return "unknown";
}

/**
 * Can this device play encrypted media at all?
 *
 * Asked of the device rather than inferred from a platform string. `requestMedia
 * KeySystemAccess` is the standard question and the only honest one: a WebView
 * that reports Android and has no Widevine CDM exists, and a `if (android)`
 * would have promised on its behalf.
 *
 * Resolves false rather than throwing on anything unexpected - a device that
 * cannot answer cannot be relied on to play, and the surface hides uniformly.
 */
export async function drmSupported(
  keySystem = "com.widevine.alpha"
): Promise<boolean> {
  const nav =
    typeof navigator === "undefined"
      ? undefined
      : (navigator as unknown as {
          requestMediaKeySystemAccess?: (
            keySystem: string,
            configs: unknown[]
          ) => Promise<unknown>;
        });
  if (typeof nav?.requestMediaKeySystemAccess !== "function") return false;
  try {
    await nav.requestMediaKeySystemAccess(keySystem, [
      {
        initDataTypes: ["cenc"],
        videoCapabilities: [{ contentType: 'video/mp4; codecs="avc1.42E01E"' }],
      },
    ]);
    return true;
  } catch {
    return false;
  }
}

/**
 * The DRM servers block for `player.configure`.
 *
 * Driven entirely by what the play endpoint returned - never hardcoded and never
 * platform-branched. The stream is JIT-packaged with FairPlay, PlayReady and
 * Widevine in one manifest, so the choice of CDM is the device's; what we supply
 * is where each one may send a challenge.
 *
 * PlayReady is included when the caller has an endpoint for it. Foyer mints
 * Widevine only today (`verimatrixLicenceUrl`), so in practice this is a
 * one-entry map - but the shape is the one the reference player uses, and
 * hardcoding a single key system here is how a platform branch gets in through
 * the back door later.
 */
export function drmServers(licence: {
  widevineUrl?: string | null;
  playreadyUrl?: string | null;
}): Record<string, string> {
  const servers: Record<string, string> = {};
  if (licence.widevineUrl) servers["com.widevine.alpha"] = licence.widevineUrl;
  if (licence.playreadyUrl) {
    servers["com.microsoft.playready"] = licence.playreadyUrl;
  }
  return servers;
}

/* -------------------------------------------------------------------------- */
/* The ABR cap (PLAYER-4)                                                     */
/* -------------------------------------------------------------------------- */

/**
 * ## Why a player that climbs freely is worse than no player
 *
 * Catalogue streams are an ABR ladder from Agile's origin, **authored for the
 * vendor app's native player, not for a WebView**. Shaka's adaptation logic
 * measures throughput and picks the highest rendition the *network* can carry -
 * it has no opinion about what the SoC can decode. On a Philips MediaSuite that
 * is exactly the wrong instinct: measured on real hardware on 2026-08-03
 * (`docs/memory/global/pitfalls/panel-video-encoding.md`), H.264 **High@4.2 at
 * 12.3 Mbps plays about five seconds and then stalls**, and Main@4.0 at
 * <=6 Mbps with no B-frames plays. A hotel's uplink carries 12 Mbps
 * comfortably, so throughput-only adaptation will climb straight into the
 * failure and stay there.
 *
 * A picture that runs for five seconds and freezes is worse than a refusal: it
 * looks like the network, it is reported as the network, and nobody looks at
 * the encode. So the ceiling is expressed here, once, as configuration.
 *
 * ## Capability, never platform - and never a vendor's own claim, either
 *
 * There is no `if (mediasuite)`. The ceiling is *asked of the device* through
 * `navigator.mediaCapabilities.decodingInfo()` ({@link probeDecodeSupport}),
 * the same doctrine {@link drmSupported} follows.
 *
 * But the device's answer is only allowed to move the cap **down**. That is the
 * single most important rule in this file, and it is there because the panel
 * that stalls would have answered "yes" to every question asked here: a
 * WebView reporting `supported: true, smooth: true` for High@4.2 is precisely
 * the hardware we measured stalling on it. A browser's self-assessment is a
 * hint, not a measurement.
 *
 * The one thing that may raise the cap is a {@link RecordedVerdict} - a result
 * somebody actually took at a panel and posted to
 * `POST /api/foyer/screens/:id/tv/probe-result`, keyed on model + firmware. A
 * device class that measures better is not permanently held down by a default
 * derived from a different panel; it is held down until somebody measures it.
 *
 * ⚠️ **Nothing here has been measured on the panel it will govern.** Suites B
 * and C of `docs/player-hardware-tests.md` (the decode ladder and the 50-cycle
 * decoder-release soak) run on hardware. What ships is the mechanism plus a
 * stated conservative default - see `docs/player-drm-spike.md`.
 */

/** One rung of a decode ladder: a rendition, described well enough to ask about. */
export interface DecodeRung {
  /** Stable id - the manifest's Representation id where there is one. */
  id: string;
  /** Peak bitrate in **bits per second** (DASH `@bandwidth`). */
  bitrate: number;
  width: number;
  height: number;
  /** RFC 6381 codec string, e.g. `avc1.4d4028` = H.264 Main@4.0. */
  codecs: string;
  /** Frames per second. Defaults to 25 when the manifest does not say. */
  frameRate?: number;
  /** `video/mp4` unless the manifest says otherwise. */
  mimeType?: string;
}

/** What the device said about one rung. All three flags come from the browser. */
export interface RungSupport {
  rung: DecodeRung;
  supported: boolean;
  smooth: boolean;
  powerEfficient: boolean;
}

/**
 * A result somebody took standing at a panel, keyed on model + firmware.
 *
 * Mirrors what `POST /api/foyer/screens/:id/tv/probe-result` stores. "It worked
 * on a MediaSuite" is not a finding; the model and the firmware are what make
 * it one, and they are carried here so a cap can never be applied to hardware
 * it was not measured on.
 */
export interface RecordedVerdict {
  model: string;
  firmware: string;
  /**
   * Highest bitrate (bits/s) a rendition **played** at on this device class,
   * or null when the decode ladder has not been run.
   *
   * Null means "not measured" and leaves the default in place. **Zero means
   * measured, and nothing played** - the Samsung LFD result - and it is a
   * legitimate verdict: it restricts every rendition, Shaka raises 4012, and
   * the screen says so instead of showing a black rectangle for two minutes.
   */
  maxPlayableBitrate: number | null;
  /** Highest pixel count that played, or null when not measured. */
  maxPlayablePixels: number | null;
}

/** Where a cap came from. Rendered in diagnostics; never inferred from a UA. */
export type AbrCapSource =
  /** Nobody has measured this device class. The numbers below are the floor. */
  | "conservative_default"
  /** The device's own `decodingInfo` answers lowered it. */
  | "device_probe"
  /** Somebody ran the probe at a panel of this model + firmware. */
  | "recorded_verdict";

export interface AbrCap {
  /** Bits per second. Shaka's `restrictions.maxBandwidth`. */
  maxBitrate: number;
  /**
   * Width x height, rather than a max width and a max height.
   *
   * Portrait matters here: `panel-video-encoding` measured **1080x1920 portrait
   * is fine** on the MediaSuite and explicitly ruled out the frame-height
   * theory. A `maxHeight: 1088` would have restricted a rendition we have
   * evidence plays. Pixels is the constraint the decoder actually has, and it
   * is orientation-blind.
   */
  maxPixels: number;
  source: AbrCapSource;
  /** One sentence for a diagnostics screen. Never shown to a guest. */
  reason: string;
}

/** 1920x1088 - the classic H.264 macroblock-aligned 1080p decoder maximum. */
const HD_PIXELS = 1920 * 1088;

/**
 * The floor every panel starts on: **Main@4.0, <=6 Mbps, 1080p**.
 *
 * Not a guess and not a round number. It is the encode that was re-encoded TO
 * on 2026-08-03 and observed to play, after High@4.2 / 12.3 Mbps was observed
 * to stall - the one measurement the platform actually holds. It is deliberately
 * the *known-good* encode rather than the *last-known-bad minus a margin*: the
 * bad one stalled after five seconds, so the true ceiling is somewhere between
 * the two and nobody has narrowed it.
 */
export const CONSERVATIVE_ABR_CAP: AbrCap = {
  maxBitrate: 6_000_000,
  maxPixels: HD_PIXELS,
  source: "conservative_default",
  reason:
    "no measurement for this device class - held at the known-good 1080p / 6 Mbps encode",
};

/** `video/mp4; codecs="avc1.4d4028"`, the shape `decodingInfo` wants. */
export function rungContentType(rung: DecodeRung): string {
  return `${rung.mimeType ?? "video/mp4"}; codecs="${rung.codecs}"`;
}

/**
 * Ask the device about each rung of a ladder.
 *
 * `mediaCapabilities.decodingInfo` is the standard question and the only one
 * that is not a platform string. `type: "media-source"` because that is how
 * Shaka plays - a `file` answer is about progressive download and can differ.
 *
 * Every failure resolves to "this rung was not answered" rather than throwing:
 * a device that cannot answer must not be assumed capable, and an absent answer
 * simply leaves {@link chooseAbrCap} on its conservative default.
 */
export async function probeDecodeSupport(
  rungs: readonly DecodeRung[]
): Promise<RungSupport[]> {
  const caps =
    typeof navigator === "undefined"
      ? undefined
      : (
          navigator as unknown as {
            mediaCapabilities?: {
              decodingInfo?: (config: unknown) => Promise<{
                supported?: boolean;
                smooth?: boolean;
                powerEfficient?: boolean;
              }>;
            };
          }
        ).mediaCapabilities;
  if (typeof caps?.decodingInfo !== "function") return [];

  const answers: RungSupport[] = [];
  for (const rung of rungs) {
    try {
      const info = await caps.decodingInfo({
        type: "media-source",
        video: {
          contentType: rungContentType(rung),
          width: rung.width,
          height: rung.height,
          bitrate: rung.bitrate,
          framerate: rung.frameRate ?? 25,
        },
      });
      answers.push({
        rung,
        supported: info.supported === true,
        smooth: info.smooth === true,
        powerEfficient: info.powerEfficient === true,
      });
    } catch {
      // An unanswered rung is not a "no" - it is silence, and silence is
      // already handled by the default. Recording it as unsupported would let
      // one throwing rung drag the whole cap down.
    }
  }
  return answers;
}

/**
 * The cap this panel should run under. **Pure**, so it is testable without a
 * decoder - which is the point, because nobody can put a decoder in CI.
 *
 * The order of precedence, and why:
 *
 *  1. **A recorded verdict wins outright.** Somebody stood at a panel of this
 *     model and firmware and watched. That beats every inference, including
 *     ours, and it is the only input allowed to raise the cap.
 *  2. **Otherwise the device's own answers may only lower it.** The highest
 *     rung the device calls supported + smooth + power-efficient becomes the
 *     ceiling if it is *below* the default. Power-efficient is preferred
 *     because software fallback is the mechanism behind the measured stall -
 *     but it is not required, because plenty of honest WebViews report it false
 *     for everything and requiring it would cripple hardware that is fine.
 *  3. **Otherwise the conservative default**, unchanged.
 */
export function chooseAbrCap(
  input: {
    verdict?: RecordedVerdict | null;
    probe?: readonly RungSupport[] | null;
  } = {}
): AbrCap {
  const verdict = input.verdict;
  if (verdict && verdict.maxPlayableBitrate !== null) {
    return {
      maxBitrate: verdict.maxPlayableBitrate,
      // A verdict that measured bitrate but not resolution keeps the default
      // pixel ceiling: half a measurement does not license the other half.
      //
      // `||` and not `??` on purpose. A probe that reports a played rung
      // without dimensions produces a pixel count of ZERO, and zero pixels
      // restricts every rendition in every manifest - a whole device class
      // permanently told it cannot play video, from one sloppy POST. The
      // bitrate is where a "nothing plays" verdict is expressed; the pixel
      // ceiling has no business inventing one.
      maxPixels: verdict.maxPlayablePixels || CONSERVATIVE_ABR_CAP.maxPixels,
      source: "recorded_verdict",
      reason: `measured on ${verdict.model} firmware ${verdict.firmware}`,
    };
  }

  const answers = input.probe ?? [];
  if (answers.length === 0) return CONSERVATIVE_ABR_CAP;

  const smooth = answers.filter((a) => a.supported && a.smooth);
  const efficient = smooth.filter((a) => a.powerEfficient);

  let best: RungSupport;
  let why: string;
  if (efficient.length > 0) {
    best = highest(efficient);
    why = "highest rendition the device calls power-efficient";
  } else if (smooth.length > 0) {
    best = highest(smooth);
    why =
      "device reports nothing power-efficient; held to its highest smooth rendition";
  } else {
    // Nothing the device will even claim to play smoothly. Drop to the smallest
    // rung it was asked about rather than to the default: a panel this weak is
    // the LFD case, and handing it 6 Mbps is handing it the failure.
    best = lowest(answers);
    why = "device claims no rendition plays smoothly; held to the lowest rung";
  }

  const bitrate = Math.min(best.rung.bitrate, CONSERVATIVE_ABR_CAP.maxBitrate);
  const pixels = Math.min(
    best.rung.width * best.rung.height,
    CONSERVATIVE_ABR_CAP.maxPixels
  );
  if (
    bitrate >= CONSERVATIVE_ABR_CAP.maxBitrate &&
    pixels >= CONSERVATIVE_ABR_CAP.maxPixels
  ) {
    // The device answered, and its answer was no tighter than the default.
    // Reported as the default so a diagnostics screen does not imply the panel
    // was measured when it was only asked.
    return CONSERVATIVE_ABR_CAP;
  }
  return {
    maxBitrate: bitrate,
    maxPixels: pixels,
    source: "device_probe",
    reason: `${why} (${Math.round(bitrate / 1000)} kbps)`,
  };
}

function highest(answers: readonly RungSupport[]): RungSupport {
  return answers.reduce((a, b) => (b.rung.bitrate > a.rung.bitrate ? b : a));
}

function lowest(answers: readonly RungSupport[]): RungSupport {
  return answers.reduce((a, b) => (b.rung.bitrate < a.rung.bitrate ? b : a));
}

/**
 * The cap as a `player.configure()` block.
 *
 * Both halves are set on purpose. `abr.restrictions` steers the adaptation
 * logic; the top-level `restrictions` makes the over-cap renditions
 * **unselectable at all**, which is what stops a manual track selection (or a
 * future quality menu) from reaching around the cap and picking the rendition
 * that stalls.
 *
 * When a manifest has nothing under the cap, Shaka raises
 * `RESTRICTIONS_CANNOT_BE_MET` (4012) and {@link classifyStreamError} names it
 * `codec_unsupported`. That is the intended outcome: a named refusal is a
 * support ticket somebody can act on, and five seconds of picture followed by a
 * freeze is not.
 */
export function abrCapConfig(cap: AbrCap): {
  abr: { restrictions: { maxBandwidth: number; maxPixels: number } };
  restrictions: { maxBandwidth: number; maxPixels: number };
} {
  const restrictions = {
    maxBandwidth: cap.maxBitrate,
    maxPixels: cap.maxPixels,
  };
  return { abr: { restrictions: { ...restrictions } }, restrictions };
}
